import React, { useState, useEffect, useMemo } from 'react';
import { sendFrontAction } from '../../services/api';
import { validateName, validatePhone, validateEmail, normalizePhoneUZ, hasErrors } from '../../utils/validation';
import './ShowroomAppointment.css'; 

// Список доступных шоурумов (при необходимости можно получать динамически с бэкенда)
const SHOWROOMS = [
    {
        id: 1,
        name: 'PREMIUM AUTO',
        address: 'г. Ташкент, Мирабадский район, ул. Авлио Ота 128',
        mapUrl: 'https://yandex.uz/maps/10335/tashkent/?ll=69.281679%2C41.288616&mode=poi&poi%5Bpoint%5D=69.281213%2C41.288841&poi%5Buri%5D=ymapsbm1%3A%2F%2Forg%3Foid%3D126436150045&z=18.8' // Ссылка на карты
    }
];

const AVAILABLE_TIMES = [
    '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', 
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', 
    '18:00', '18:30', '19:00'
];

const ShowroomAppointment = () => {
    const [step, setStep] = useState(1); 
    const [selectedDealer, setSelectedDealer] = useState(null);
    const [selectedService, setSelectedService] = useState('');
    const [selectedSubSection, setSelectedSubSection] = useState('');
    const [selectedCar, setSelectedCar] = useState(null);
    const [carFilter, setCarFilter] = useState('Все модели');
    
    // --- СОСТОЯНИЯ ДЛЯ ДАННЫХ С БЭКЕНДА ---
    const [backendCars, setBackendCars] = useState([]); 
    const [filteredCars, setFilteredCars] = useState([]); 
    const [isLoading, setIsLoading] = useState(false);

    // --- НАСТРОЙКИ ДИНАМИЧЕСКОГО КАЛЕНДАРЯ ---
    const [today] = useState(() => new Date()); 
    const [currentMonth, setCurrentMonth] = useState(today.getMonth()); 
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [selectedDate, setSelectedDate] = useState(null); 
    const [selectedTime, setSelectedTime] = useState('');
    
    const [isCarDetailsOpen, setIsCarDetailsOpen] = useState(false);
    
    // Состояние для модального окна
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

    // Контактные данные пользователя
    const [formData, setFormData] = useState({
        lastName: '',
        firstName: '',
        phone: '',
        email: '',
        purchasePlan: 'в течение 1 мес',
        feedbackMethod: 'Да (телефон)'
    });

    const [errors, setErrors] = useState({ lastName: '', firstName: '', phone: '', email: '' });

    // --- ЗАГРУЗКА ДАННЫХ С БЭКЕНДА ---
    useEffect(() => {
        const fetchCarData = async () => {
            setIsLoading(true);
            try {
                const response = await sendFrontAction('showroom');

                if (response && response.success && response.data) {
                    setBackendCars(response.data);
                } else if (Array.isArray(response)) {
                    setBackendCars(response);
                }
            } catch (error) {
                console.error("Ошибка при получении данных с бэкенда:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCarData();
    }, []);

    // --- ФИЛЬТРАЦИЯ ДАННЫХ НА ОСНОВЕ ВЫБРАННОЙ УСЛУГИ ---
    useEffect(() => {
        let result = [...backendCars];

        if (selectedService === 'Приобретение нового автомобиля') {
            result = result.filter(car => car.category === 'new' && car.type !== 'motorcycle');
            setSelectedSubSection('Автомобили в наличии');
        } else if (selectedService === 'Приобретение автомобиля с пробегом') {
            result = result.filter(car => car.category === 'used');
            setSelectedSubSection('Автомобили в наличии');
        } else if (selectedService === 'Приобретение нового мотоцикла') {
            result = result.filter(car => car.type === 'motorcycle' || car.category === 'moto');
            setSelectedSubSection('Мотоциклы в наличии');
        }

        // Оставляем только технику в наличии
        result = result.filter(car => car.status === 'В наличии' || car.in_stock === true || car.in_stock === 1);

        setFilteredCars(result);
        setSelectedCar(null); 
        setCarFilter('Все модели');
    }, [selectedService, backendCars]);

    // --- ДИНАМИЧЕСКИЕ ВКЛАДКИ ФИЛЬТРОВ ---
    const availableFilterTabs = useMemo(() => {
        const categories = new Set();
        
        filteredCars.forEach(car => {
            const cat = car.series || car.body_type || car.type_name || car.category_name;
            if (cat && cat !== 'new' && cat !== 'used' && cat !== 'moto' && cat !== 'motorcycle') {
                categories.add(cat);
            }
        });

        return ['Все модели', ...Array.from(categories)];
    }, [filteredCars]);

    // Обработка выбора дилера (Шаг 1)
    const handleSelectDealer = (dealer) => {
        setSelectedDealer(dealer);
        setStep(2);
    };

    // Обработка выбора услуги (Шаг 2)
    const handleSelectService = (serviceName) => {
        setSelectedService(serviceName);
        setStep(3);
    };

    // --- ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ КАЛЕНДАРЯ И ФОРМЫ ---
    const monthsRu = [
        'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];

    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayIndex = (new Date(currentYear, currentMonth, 1).getDay() + 6) % 7;

    const handlePrevMonth = () => {
        if (currentYear === today.getFullYear() && currentMonth === today.getMonth()) return;
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(prev => prev - 1);
        } else {
            setCurrentMonth(prev => prev - 1);
        }
    };

    const handleNextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(prev => prev + 1);
        } else {
            setCurrentMonth(prev => prev + 1);
        }
    };

    const handleDateSelect = (day) => {
        const clickedDate = new Date(currentYear, currentMonth, day);
        const tempToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        
        if (clickedDate < tempToday) return;
        setSelectedDate(clickedDate);
        setSelectedTime(''); 
    };

    const isToday = (day) => {
        return today.getDate() === day && today.getMonth() === currentMonth && today.getFullYear() === currentYear;
    };

    const isPast = (day) => {
        const dateToCheck = new Date(currentYear, currentMonth, day);
        const tempToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        return dateToCheck < tempToday;
    };

    const isSelected = (day) => {
        if (!selectedDate) return false;
        return selectedDate.getDate() === day && 
               selectedDate.getMonth() === currentMonth && 
               selectedDate.getFullYear() === currentYear;
    };

    const isTimeSlotDisabled = (time) => {
        if (!selectedDate) return true;
        
        const isSelectedDayToday = 
            selectedDate.getDate() === today.getDate() &&
            selectedDate.getMonth() === today.getMonth() &&
            selectedDate.getFullYear() === today.getFullYear();

        if (isSelectedDayToday) {
            const now = new Date();
            const [slotHours, slotMinutes] = time.split(':').map(Number);
            
            const currentTotalMinutes = now.getHours() * 60 + now.getMinutes();
            const slotTotalMinutes = slotHours * 60 + slotMinutes;

            return slotTotalMinutes <= currentTotalMinutes; 
        }
        
        return false;
    };

    const handleTimeClick = (time) => {
        setSelectedTime(time);
    };

    const formatDateString = (date) => {
        if (!date) return '';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${day}.${month}.${date.getFullYear()}`;
    };

    const handleBack = () => {
        if (step > 1) {
            setStep(step - 1);
        }
    };

    const resetToStep = (targetStep) => {
        if (step > targetStep) setStep(targetStep);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'lastName') setErrors(prev => ({ ...prev, lastName: validateName(value) }));
        if (name === 'firstName') setErrors(prev => ({ ...prev, firstName: validateName(value) }));
        if (name === 'phone') setErrors(prev => ({ ...prev, phone: validatePhone(value) }));
        if (name === 'email') setErrors(prev => ({ ...prev, email: validateEmail(value) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = {
            lastName: validateName(formData.lastName),
            firstName: validateName(formData.firstName),
            phone: validatePhone(formData.phone),
            email: validateEmail(formData.email),
        };
        setErrors(validationErrors);

        if (hasErrors(validationErrors)) {
            alert('Пожалуйста, проверьте правильность заполнения полей формы.');
            return;
        }

        try {
            const formattedDate = selectedDate ? selectedDate.toISOString().split('T')[0] : null;
            const payload = {
                dealer: selectedDealer?.name,
                service: selectedService,
                subSection: selectedSubSection,
                carId: selectedCar?.id,
                carName: selectedCar?.name,
                date: formattedDate,
                time: selectedTime,
                ...formData,
                phone: normalizePhoneUZ(formData.phone), // 👈 нормализованный номер
            };
            const response = await sendFrontAction('showroom', payload);
            if (response && response.success) {
                setIsSuccessModalOpen(true);
            } else {
                alert(response?.message || "Произошла ошибка при сохранении записи");
            }
        } catch (error) {
            console.error("Ошибка при создании записи:", error);
            alert("Не удалось отправить запись. Пожалуйста, попробуйте позже.");
        }
    };

    const handleCloseModal = () => {
        setIsSuccessModalOpen(false);
        // При желании можно сбросить форму и шаг:
        // setStep(1);
    };

    const displayedCars = filteredCars.filter(car => {
        if (carFilter === 'Все модели') return true;
        const carCategory = car.series || car.body_type || car.type_name || car.category_name;
        return carCategory === carFilter;
    });

    return (
        <div className="appointment-container">
            {/* Хлебные крошки и Кнопка Назад */}
            {step > 1 && (
                <div className="appointment-navigation">
                    <button className="back-button" onClick={handleBack}>
                        <span className="arrow-left">‹</span>Назад
                    </button>
                    <div className="breadcrumbs">
                        {step >= 2 && <span onClick={() => resetToStep(1)}> / {selectedDealer?.name}</span>}
                        {step >= 3 && <span onClick={() => resetToStep(2)}> / {selectedService}</span>}
                        {step >= 4 && <span onClick={() => resetToStep(3)}> / {selectedCar?.name}</span>}
                        {step === 5 && <span> / {selectedCar?.name ? ` ${selectedCar.name}` : ''} / Контактные данные</span>}
                    </div>
                </div>
            )}

            <h1 className="main-title">ОНЛАЙН-ЗАПИСЬ НА ВИЗИТ В ОТДЕЛ ПРОДАЖ</h1>

            {/* Аккордеон с подробной информацией о выбранном авто */}
            {step >= 4 && selectedCar && (
                <div className="car-accordion">
                    <div className="accordion-header" onClick={() => setIsCarDetailsOpen(!isCarDetailsOpen)}>
                        <div className="header-info">
                            <img src={selectedCar.image} alt="" className="mini-car-thumb" />
                            <strong>{selectedCar.name}</strong>
                        </div>
                        <span className={`arrow-icon ${isCarDetailsOpen ? 'open' : ''}`}>▼</span>
                    </div>
                    
                    {isCarDetailsOpen && (
                        <div className="accordion-content">
                            <div className="detailed-view">
                                <img src={selectedCar.image} alt={selectedCar.name} className="large-preview" />
                                <div className="specs-list">
                                    <p className="total-sum">Сумма: <span>{selectedCar.price}</span></p>
                                    {selectedCar.info && (
                                        <>
                                            <div className="spec-row"><span>Двигатель:</span> <span>{selectedCar.info.engine || selectedCar.engine}</span></div>
                                            <div className="spec-row"><span>КПП:</span> <span>{selectedCar.info.transmission || selectedCar.transmission}</span></div>
                                            <div className="spec-row"><span>Кузов:</span> <span>{selectedCar.info.body || selectedCar.body}</span></div>
                                            <div className="spec-row"><span>Цвет кузова:</span> <span>{selectedCar.info.color || selectedCar.color}</span></div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Лоадер */}
            {isLoading && (
                <div className="loading-overlay">
                    <div className="spinner">Загрузка данных...</div>
                </div>
            )}

            {/* --- ШАГ 1: ВЫБОР ШОУРУМА --- */}
            {step === 1 && !isLoading && (
                <div className="step-wrapper">
                    <p className="step-subtitle">Выберите шоурум</p>
                    <div className="dealers-list">
                        {SHOWROOMS.map((dealer) => (
                            <div 
                                key={dealer.id} 
                                className="dealer-card"
                                onClick={() => handleSelectDealer(dealer)}
                            >
                                <div className="dealer-card-body">
                                    <h3 className="dealer-title">{dealer.name}</h3>
                                </div>
                                <div className="dealer-card-footer">
                                    <span className="dealer-address">{dealer.address}</span>
                                    <a 
                                        href={dealer.mapUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="map-link"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        На карте
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* --- ШАГ 2: ВЫБОР УСЛУГИ --- */}
            {step === 2 && (
                <div className="step-wrapper">
                    <p className="step-subtitle">Выберите причину обращения</p>
                    <div className="menu-grid">
                        <div className="menu-item" onClick={() => handleSelectService('Приобретение нового автомобиля')}>
                            <div className="icon-box">🚘</div>
                            <span>Приобретение нового автомобиля</span>
                        </div>
                        <div className="menu-item" onClick={() => handleSelectService('Приобретение автомобиля с пробегом')}>
                            <div className="icon-box">🚗</div>
                            <span>Приобретение автомобиля с пробегом</span>
                        </div>
                        <div className="menu-item" onClick={() => handleSelectService('Приобретение нового мотоцикла')}>
                            <div className="icon-box">🏍️</div>
                            <span>Приобретение нового мотоцикла</span>
                        </div>
                    </div>
                </div>
            )}

            {/* --- ШАГ 3: ВЫБОР МОДЕЛИ --- */}
            {step === 3 && (
                <div className="step-wrapper">
                    <p className="step-subtitle">
                        {selectedService === 'Приобретение нового мотоцикла' ? 'Выберите мотоцикл' : 'Выберите автомобиль'}
                    </p>
                    
                    {/* Динамические фильтры категорий */}
                    {availableFilterTabs.length > 1 && (
                        <div className="filter-tabs">
                            {availableFilterTabs.map((tab) => (
                                <button 
                                    key={tab} 
                                    className={`tab-btn ${carFilter === tab ? 'active' : ''}`}
                                    onClick={() => setCarFilter(tab)}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="cars-grid">
                        {displayedCars.length === 0 ? (
                            <p className="no-cars-message">Нет доступных предложений по выбранным критериям.</p>
                        ) : (
                            displayedCars.map((car) => (
                                <div 
                                    key={car.id} 
                                    className={`car-card ${selectedCar?.id === car.id ? 'selected' : ''}`}
                                    onClick={() => setSelectedCar(car)}
                                >
                                    <span className="status-badge">● {car.status || 'В наличии'}</span>
                                    <h3 className="car-name">{car.name}</h3>
                                    <p className="car-price">{car.price}</p>
                                    <div className="car-img-container">
                                        <img src={car.image} alt={car.name} />
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {selectedCar && (
                        <div className="sticky-bottom-bar">
                            <button className="primary-action-btn" onClick={() => setStep(4)}>
                                Выбрать дату и время ➔
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* --- ШАГ 4: ВЫБОР ДАТЫ И ВРЕМЕНИ --- */}
            {step === 4 && (
                <div className="td-step-wrapper animate-fade-in">
                    <div className="td-calendar-navigation">
                        <button 
                            type="button"
                            className={`td-month-nav-btn ${currentYear === today.getFullYear() && currentMonth === today.getMonth() ? 'disabled' : ''}`}
                            onClick={handlePrevMonth}
                            disabled={currentYear === today.getFullYear() && currentMonth === today.getMonth()}
                        >
                            ‹
                        </button>
                        <h2 className="td-calendar-title">
                            {monthsRu[currentMonth]} {currentYear}
                        </h2>
                        <button 
                            type="button"
                            className="td-month-nav-btn"
                            onClick={handleNextMonth}
                        >
                            ›
                        </button>
                    </div>
                    
                    <div className="td-calendar-grid">
                        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(d => (
                            <div key={d} className="td-day-header">{d}</div>
                        ))}
                        
                        {Array.from({ length: firstDayIndex }).map((_, idx) => (
                            <div key={`empty-${idx}`} className="td-calendar-day empty"></div>
                        ))}
                        
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                            const day = i + 1;
                            const isPastDay = isPast(day);
                            const isSelectedDay = isSelected(day);

                            return (
                                <div 
                                    key={day} 
                                    className={`td-calendar-day ${isSelectedDay ? 'active-day' : ''} ${isPastDay ? 'disabled-day' : ''} ${isToday(day) ? 'today-day' : ''}`}
                                    onClick={() => { if (!isPastDay) handleDateSelect(day); }}
                                >
                                    {day}
                                </div>
                            );
                        })}
                    </div>

                    {selectedDate && (
                        <div className="td-time-slots-section animate-fade-in">
                            <p className="td-step-label">Доступное время для визита в шоурум</p>
                            <div className="td-time-grid">
                                {AVAILABLE_TIMES.map(time => {
                                    const isDisabled = isTimeSlotDisabled(time);
                                    return (
                                        <button 
                                            key={time} 
                                            type="button"
                                            disabled={isDisabled}
                                            className={`td-time-btn ${selectedTime === time ? 'active-time' : ''} ${isDisabled ? 'time-disabled' : ''}`}
                                            onClick={() => handleTimeClick(time)}
                                        >
                                            {time}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {selectedTime && (
                        <div className="td-action-footer animate-fade-in">
                            <button 
                                type="button" 
                                className="td-primary-btn" 
                                onClick={() => setStep(5)}
                            >
                                Далее <span className="arrow-right">➔</span>
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* --- ШАГ 5: ВВОД ДАННЫХ ПОЛЬЗОВАТЕЛЯ --- */}
            {step === 5 && (
                <form onSubmit={handleSubmit} className="step-wrapper user-form-wrapper animate-fade-in">
                    <div className="form-group">
                        <label>Фамилия *</label>
                        <input
                            type="text"
                            name="lastName"
                            required
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className={errors.lastName ? 'field-invalid' : ''}
                        />
                        {errors.lastName && <span className="field-error">{errors.lastName}</span>}
                    </div>

                    <div className="form-group">
                        <label>Имя *</label>
                        <input
                            type="text"
                            name="firstName"
                            required
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className={errors.firstName ? 'field-invalid' : ''}
                        />
                        {errors.firstName && <span className="field-error">{errors.firstName}</span>}
                    </div>

                    <div className="form-group">
                        <label>Телефон *</label>
                        <div className="phone-input-container">
                            <span className="country-flag">🇺🇿</span>
                            <span className="country-code">+998</span>
                            <input
                                type="tel"
                                name="phone"
                                placeholder="(90) 123-45-67"
                                required
                                value={formData.phone}
                                onChange={handleInputChange}
                                className={errors.phone ? 'field-invalid' : ''}
                            />
                        </div>
                        {errors.phone && <span className="field-error">{errors.phone}</span>}
                    </div>

                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={errors.email ? 'field-invalid' : ''}
                        />
                        {errors.email && <span className="field-error">{errors.email}</span>}
                    </div>

                    <div className="form-group">
                        <label className="section-label">Когда планируете покупку?</label>
                        <div className="radio-tile-group">
                            {['в течение 1 мес', 'в течение 6 мес', 'в течение 12 мес', 'не планирую покупку'].map((plan) => (
                                <label key={plan} className="radio-label">
                                    <input 
                                        type="radio" 
                                        name="purchasePlan" 
                                        value={plan} 
                                        checked={formData.purchasePlan === plan} 
                                        onChange={handleInputChange} 
                                    />
                                    <span>{plan}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="summary-alert-box">
                        <p className="summary-text-title">
                            Ваша запись на <strong>{formatDateString(selectedDate)}</strong>, в <strong>{selectedTime}</strong>
                        </p>
                        <p className="summary-text-sub">{selectedDealer?.name} • {selectedCar?.name} • {selectedService}</p>
                    </div>

                    <div className="form-group">
                        <label className="section-label">Необходима ли Вам обратная связь?*</label>
                        <div className="radio-tile-group inline-radio">
                            {['Нет', 'Да (телефон)', 'Viber', 'Telegram', 'Email'].map((method) => (
                                <label key={method} className="radio-label">
                                    <input 
                                        type="radio" 
                                        name="feedbackMethod" 
                                        value={method} 
                                        checked={formData.feedbackMethod === method} 
                                        onChange={handleInputChange} 
                                    />
                                    <span>{method}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-submit-container">
                        <button type="submit" className="primary-action-btn success static-btn">
                            Отправить ➔
                        </button>
                    </div>
                </form>
            )}

            {/* --- ВСПЛЫВАЮЩЕЕ ОКНО (POPUP / MODAL) --- */}
            {isSuccessModalOpen && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={handleCloseModal}>×</button>
                        <div className="modal-header">
                            <div className="modal-icon-success">✓</div>
                            <h2>Запись успешно создана!</h2>
                        </div>
                        <div className="modal-body">
                            <div className="modal-info-row">
                                <span>Шоурум:</span> <strong>{selectedDealer?.name}</strong>
                            </div>
                            <div className="modal-info-row">
                                <span>Имя:</span> <strong>{formData.firstName} {formData.lastName}</strong>
                            </div>
                            <div className="modal-info-row">
                                <span>Телефон:</span> <strong>+998 {formData.phone}</strong>
                            </div>
                            <div className="modal-info-row">
                                <span>Дата и время:</span> <strong>{formatDateString(selectedDate)} в {selectedTime}</strong>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-primary-btn" onClick={handleCloseModal}>
                                Отлично
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShowroomAppointment;